/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as ActionTypes from "constants/ActionTypes";
import TranscriptionState from "constants/TranscriptionState";
import {
  mapTrackToAsset,
  populateTranscript,
} from "utilities/ProjectUtilities";
import { AudioBufferState } from "constants/AudioBufferState";
import { Asset, AssetParticipant, AssetSet } from "types/Asset";
import { MediaType } from "@phonos/types";
import { AssetsAction } from "types/Actions";
import { v4 as uuidv4 } from "uuid";
import { editTranscriptDisplayName } from "utilities/TranscriptEditUtilities";
import { updateIfExists } from "reducers/utilities/UpdateIfExists";

export default function AssetsReducer(
  assets: AssetSet = {},
  action: AssetsAction
): AssetSet {
  switch (action.type) {
    case ActionTypes.LOAD_PROJECT:
      return action.assets;
    case ActionTypes.UPDATE_LOCAL_TRANSCRIPT:
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          transcript: action.transcript,
          transcriptVersion: uuidv4(),
        },
      };
    case ActionTypes.UPDATE_SPEAKER_DISPLAY_NAME: {
      const asset = assets[action.assetId];
      const participants = asset.participants || [];
      const existingParticipant = participants.find(
        (participant) =>
          participant.unidentifiedSpeakerNumber ===
          action.unidentifiedSpeakerNumber
      );
      const transcript = asset.transcript;

      let participantMedia = existingParticipant?.media;
      if (!participantMedia && asset.mediaType === MediaType.VIDEO) {
        participantMedia = {
          id: asset.id,
          mediaType: MediaType.VIDEO,
          streamingVideo360PUrl: asset.streamingVideo360PUrl,
        };
      }

      const prevParticipants = participants.filter(
        (participant) =>
          participant.unidentifiedSpeakerNumber <
          action.unidentifiedSpeakerNumber
      );
      // If the participant does not exist, this would create it
      const newParticipant: AssetParticipant = {
        ...existingParticipant,
        media: participantMedia,
        id: action.trackParticipantId,
        adobeId: action.adobeId,
        displayName: action.displayName,
        isAnonymous: action.isAnonymous,
        unidentifiedSpeakerNumber: action.unidentifiedSpeakerNumber,
      };
      const afterParticipants = participants.filter(
        (participant) =>
          participant.unidentifiedSpeakerNumber >
          action.unidentifiedSpeakerNumber
      );
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          transcript: transcript
            ? editTranscriptDisplayName(
                transcript,
                action.unidentifiedSpeakerNumber,
                action.displayName
              )
            : undefined,
          transcriptVersion: uuidv4(),
          participants: [
            ...prevParticipants,
            newParticipant,
            ...afterParticipants,
          ],
        },
      };
    }
    case ActionTypes.TRANSCRIBE_ASSET_SUCCESS: {
      const assetParticipants = assets[action.assetId]?.participants || [];
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          transcriptionState: TranscriptionState.COMPLETE,
          transcript: populateTranscript(action.transcript, assetParticipants),
          transcriptionService: action.transcriptionService,
          transcriptDetectedLanguage: action.transcriptDetectedLanguage,
        },
      };
    }
    case ActionTypes.TRANSCRIBE_ASSET_PROCESS:
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          transcriptionState: TranscriptionState.PROCESSING,
          startedTranscriptionAt: action.startedTranscriptionAt,
        },
      };
    case ActionTypes.TRANSCRIBE_ASSET_EMPTY:
    case ActionTypes.TRANSCRIBE_ASSET_FAILURE:
      if (!assets[action.assetId]) {
        return assets;
      }
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          transcript: [],
          transcriptionState: TranscriptionState.PENDING,
        },
      };
    case ActionTypes.TRANSCRIBE_ASSET_CANCELLED:
      if (!assets[action.assetId]) {
        return assets;
      }
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          transcriptionState: TranscriptionState.CANCELLED,
        },
      };
    case ActionTypes.ASSET_LOADED:
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          audioBufferState: AudioBufferState.LOADED,
        },
      };
    case ActionTypes.ASSET_LOAD_FAILED:
      return {
        ...assets,
        [action.assetId]: {
          ...assets[action.assetId],
          audioBufferState: AudioBufferState.FAILED,
        },
      };
    case ActionTypes.ASSET_PROCESSING_FAILED:
      const { [action.assetId]: _, ...remainingAssets } = assets;
      return remainingAssets;
    case ActionTypes.INSERT_ASSET:
      return {
        ...assets,
        [action.asset.id]: {
          ...assets[action.asset.id],
          ...action.asset,
          transcript: [],
          transcriptionState: TranscriptionState.PENDING,
        },
      };
    case ActionTypes.ASSET_VIDEO_PROCESSING_COMPLETED:
      // This updates some of the fields on the asset, but not all of them.
      return updateIfExists(assets, action.assetId, {
        ...action.asset,
      });
    case ActionTypes.RECORDING_SESSION_TRACK_MEDIA_PROCESSING_SUCCEEDED: {
      const assetParticipants = assets[action.assetId]?.participants || [];
      const participant = assetParticipants.find(
        (participant) => participant.media?.id === action.assetMediaId
      );

      if (!participant) {
        return assets;
      }

      const participantMedia = participant.media;

      if (!participantMedia) {
        return assets;
      }

      const prevParticipants = assetParticipants.filter(
        (p) =>
          p.unidentifiedSpeakerNumber < participant.unidentifiedSpeakerNumber
      );
      const newParticipant = {
        ...participant,
        media: {
          ...participantMedia,
          streamingVideo360PUrl: action.streamingVideo360PUrl,
          videoWidth360P: action.videoWidth360P,
          videoHeight360P: action.videoHeight360P,
          offsetMs: action.offsetMs,
          durationMs: action.durationMs,
        },
      };
      const afterParticipants = assetParticipants.filter(
        (p) =>
          p.unidentifiedSpeakerNumber > participant.unidentifiedSpeakerNumber
      );
      const newAssetParticipants: AssetParticipant[] = [
        ...prevParticipants,
        newParticipant,
        ...afterParticipants,
      ];

      return updateIfExists(assets, action.assetId, {
        participants: newAssetParticipants,
      });
    }
    case ActionTypes.RECORDING_SESSION_PROCESSING_SUCCEEDED: {
      if (!action.asset) {
        return assets;
      }

      const mappedAsset = mapTrackToAsset(action.asset);

      return {
        ...assets,
        [action.asset.id]: mappedAsset,
      };
    }
    case ActionTypes.STUDIO_SOUND_ASSET_CREATED:
      if (!assets[action.originalAssetId]) {
        return { ...assets };
      }

      return {
        ...assets,
        // we want to avoid overwriting the transcript, the server is not sending it with the
        // message, but `mapTrackToAsset` will set it to undefined
        [action.originalAssetId]: {
          ...assets[action.originalAssetId],
          ...action.originalAsset,
          transcript: assets[action.originalAssetId].transcript,
        },
        [action.newAssetId]: action.newAsset,
      };
    case ActionTypes.STUDIO_ESV2_ASSET_CREATED:
      if (!assets[action.originalAssetId]) {
        return { ...assets };
      }
      return {
        ...assets,
        [action.originalAssetId]: {
          ...assets[action.originalAssetId],
          ...action.originalAsset,
          transcript: assets[action.originalAssetId].transcript,
        },
      };
    case ActionTypes.ASSET_WAVEFORM_COMPUTED:
      return updateIfExists(assets, action.assetId, {
        waveformUrl: action.waveformUrl,
      });
    case ActionTypes.ASSET_WAVEFORM_COMPUTATION_STARTED:
      return updateIfExists(assets, action.assetId, {
        waveformComputationStarted: true,
      });
    default:
      return assets;
  }
}
